import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { Pressable } from './Pressable.js';
import { useOverlayFocusTrap } from '../utils/useOverlayFocusTrap.js';
import { isTopOverlay, popOverlay, pushOverlay } from '../utils/overlayEscStack.js';
import styles from './ImageLightbox.module.css';

export type ImageLightboxProps = {
  open: boolean;
  onClose: () => void;
  src: string;
  /** Accessible dialog name (defaults to caption or “Aperçu”). */
  label?: string;
  alt?: string;
  /** Optional caption under the image (entity name, etc.). */
  caption?: string | null;
  /** Optional footer actions (change / remove / …). */
  actions?: ReactNode;
  className?: string;
};

/**
 * Full-viewport image inspect overlay.
 * Escape closes when this lightbox is the top overlay; click backdrop to dismiss.
 * Focus is trapped; body scroll is locked while open.
 */
export function ImageLightbox({
  open,
  onClose,
  src,
  label,
  alt,
  caption,
  actions,
  className,
}: ImageLightboxProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dialogLabel = (label || caption || 'Aperçu').trim() || 'Aperçu';
  const imgAlt = (alt ?? caption ?? label ?? '').trim();

  useOverlayFocusTrap(open, panelRef);

  useEffect(() => {
    if (!open) return;
    const stackId = pushOverlay();
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!isTopOverlay(stackId)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      popOverlay(stackId);
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [open, onClose]);

  if (!open || !src.trim() || typeof document === 'undefined') return null;

  return createPortal(
    <div className={cx(styles.backdrop, className)} onClick={onClose}>
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={dialogLabel}
        onClick={(e) => e.stopPropagation()}
      >
        <Pressable type="button" className={styles.close} onClick={onClose} aria-label="Fermer">
          <IconX size={20} stroke={2} aria-hidden />
        </Pressable>
        <figure className={styles.figure}>
          <img src={src} alt={imgAlt} className={styles.img} />
          {caption?.trim() ? (
            <figcaption className={styles.caption}>{caption.trim()}</figcaption>
          ) : null}
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </figure>
      </div>
    </div>,
    document.body,
  );
}

/** Shared action button class for lightbox footers (ThumbnailImagePicker, etc.). */
export const imageLightboxActionClass = styles.action;
export const imageLightboxActionDangerClass = styles.actionDanger;
